import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, Pencil, Trash2, Fuel, Gauge, Settings2, Sparkles, X, ChevronRight,
  Shield, FileText, Factory, Wrench, AlertTriangle, LogOut as ExitIcon, Calendar, Tag, Trash, Image as ImageIcon,
  Upload, Loader2,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CAR_BRANDS, PROPRIETA_TYPES, getBrandLogo, ALL_BRANDS, getModelsByBrand } from "../../lib/carBrands";
import SearchableSelect from "../../components/SearchableSelect";
import DocumentScannerBox from "../../components/DocumentScannerBox";

const STATO_BADGE = {
  disponibile: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  noleggiata: "bg-[#ff0033]/15 text-[#ff5577] border-[#ff0033]/40",
  manutenzione: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  guasto: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  fuori_servizio: "bg-white/10 text-white/60 border-white/20",
  fuori_flotta: "bg-zinc-700/30 text-white/50 border-white/15",
};

const FRAN_TEMPLATE = { franchigia_eur: 0, franchigia_perc: 0, massimale: 0 };

const emptyDatiAssicurativi = {
  compagnia: "", numero_polizza: "", broker: "",
  data_emissione: "", data_scadenza: "",
  premio_annuo: 0,
  rca: { ...FRAN_TEMPLATE },
  kasko: { ...FRAN_TEMPLATE },
  furto: { ...FRAN_TEMPLATE },
  incendio: { ...FRAN_TEMPLATE },
  atti_vandalici: false,
  cristalli: false,
  assistenza_stradale: false,
  infortuni_conducente: false,
};

const empty = {
  // Identificativi
  tipo_veicolo: "auto",
  marca: "", modello: "", targa: "", anno: 2024, anno_immatricolazione: 2024,
  km: 0, colore: "Nero", carburante: "Benzina", cambio: "Manuale",
  stato: "disponibile", motivo_fuori_uso: "",
  // Prezzi
  prezzo_giornaliero: 100, prezzo_settimanale: 600, prezzo_mensile: 2200,
  // Media
  foto: [], descrizione: "", optional: [],
  // Proprietà
  proprieta_mezzo: "aziendale",
  proprietario_nome: "", proprietario_contatto: "",
  leasing_societa: "", leasing_canone_mensile: 0, leasing_scadenza: "",
  // Assicurazione
  dati_assicurativi: { ...emptyDatiAssicurativi },
  scadenza_assicurazione: "", scadenza_revisione: "", scadenza_bollo: "", scadenza_manutenzione: "",
  // Ciclo di vita
  nr_telaio: "", numero_telaio: "",
  entrata_flotta: "", costo_acquisto: 0,
  uscita_flotta: null, // { data, causa, note, valore_realizzo }
  // Danni iniziali
  danni: [],
  // Altro
  gps_tracker: true,
};

const SECTIONS = [
  { id: "ident", label: "Identificativi", icon: Tag },
  { id: "prop",  label: "Proprietà", icon: Factory },
  { id: "ass",   label: "Assicurazione & Franchigie", icon: Shield },
  { id: "cv",    label: "Ciclo di vita", icon: Calendar },
  { id: "danni", label: "Danni iniziali", icon: AlertTriangle },
  { id: "media", label: "Foto & Descrizione", icon: ImageIcon },
];

const inputCls =
  "mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none transition-colors";
const selectCls =
  "mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-widest text-white/45";

function Field({ label, children, className = "", testid }) {
  return (
    <div className={className} data-testid={testid ? `field-${testid}` : undefined}>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}


export default function Fleet() {
  const [cars, setCars] = useState([]);
  const [search, setSearch] = useState("");
  // Vista predefinita: dashboard scadenze (no foto), su richiesta utente Feb 2026
  const [view, setView] = useState(localStorage.getItem("fleet-view") || "dashboard");

  useEffect(() => { localStorage.setItem("fleet-view", view); }, [view]);

  // Helper: badge colorato per scadenza (verde >30gg / giallo ≤30gg / rosso scaduta / grigio se mancante)
  const scadenzaBadge = (dateStr) => {
    if (!dateStr) return { cls: "bg-white/[0.04] text-white/40 border-white/10", label: "—", days: null };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
    const days = Math.round((d - today) / 86400000);
    if (days < 0) return { cls: "bg-[#ff0033]/15 text-[#ff5577] border-[#ff0033]/40", label: `Scaduta ${Math.abs(days)}gg fa`, days };
    if (days <= 30) return { cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", label: `Tra ${days}gg`, days };
    return { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", label: `Tra ${days}gg`, days };
  };

  // Stato globale veicolo: rosso se almeno 1 scadenza in rosso, giallo se almeno 1 in giallo, verde se tutte ok
  const statoScadenze = (c) => {
    const badges = [
      scadenzaBadge(c.scadenza_assicurazione),
      scadenzaBadge(c.scadenza_revisione),
      scadenzaBadge(c.scadenza_bollo),
      scadenzaBadge(c.scadenza_manutenzione),
    ];
    if (badges.some((b) => b.days !== null && b.days < 0)) return "critico";
    if (badges.some((b) => b.days !== null && b.days <= 30)) return "warning";
    if (badges.every((b) => b.days !== null && b.days > 30)) return "ok";
    return "incompleto";
  };
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [activeSection, setActiveSection] = useState("ident");

  const load = () => api.get("/cars", { params: { search } }).then((r) => setCars(r.data));
  useEffect(() => { load(); }, [search]); // eslint-disable-line

  // helper update
  const set = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        ref[keys[i]] = { ...(ref[keys[i]] || {}) };
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.marca || !form.modello || !form.targa) {
      toast.error("Marca, Modello e Targa sono obbligatori");
      setActiveSection("ident");
      return;
    }
    const payload = {
      ...form,
      anno: +form.anno,
      anno_immatricolazione: +(form.anno_immatricolazione || form.anno),
      km: +form.km,
      prezzo_giornaliero: +form.prezzo_giornaliero,
      prezzo_settimanale: +form.prezzo_settimanale,
      prezzo_mensile: +form.prezzo_mensile,
      costo_acquisto: +(form.costo_acquisto || 0),
      leasing_canone_mensile: +(form.leasing_canone_mensile || 0),
      foto: form.foto?.length ? form.foto : ["https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=800&q=80"],
    };
    try {
      if (editId) await api.put(`/cars/${editId}`, payload);
      else await api.post("/cars", payload);
      toast.success(editId ? "Auto aggiornata" : "Auto aggiunta in flotta");
      setShowForm(false); setForm(empty); setEditId(null); setActiveSection("ident");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore salvataggio"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo veicolo dalla flotta?")) return;
    await api.delete(`/cars/${id}`);
    toast.success("Veicolo eliminato"); load();
  };

  const startEdit = (c) => {
    setForm({
      ...empty,
      ...c,
      foto: c.foto || [],
      dati_assicurativi: { ...emptyDatiAssicurativi, ...(c.dati_assicurativi || {}) },
      danni: c.danni || [],
    });
    setEditId(c.id);
    setActiveSection("ident");
    setShowForm(true);
  };

  const openCreate = () => {
    setForm({ ...empty, dati_assicurativi: { ...emptyDatiAssicurativi }, danni: [] });
    setEditId(null);
    setActiveSection("ident");
    setShowForm(true);
  };

  const lookupTarga = async () => {
    if (!form.targa || form.targa.length < 5) return toast.error("Inserisci targa valida prima");
    try {
      const r = await api.get(`/targa/lookup/${form.targa}`);
      const d = r.data;
      setForm((f) => ({
        ...f,
        anno: d.anno || f.anno,
        anno_immatricolazione: d.anno_immatricolazione || f.anno_immatricolazione,
        numero_telaio: d.numero_telaio || f.numero_telaio,
        nr_telaio: d.numero_telaio || f.nr_telaio,
        scadenza_assicurazione: d.scadenza_assicurazione || f.scadenza_assicurazione,
        scadenza_revisione: d.scadenza_revisione || f.scadenza_revisione,
        scadenza_bollo: d.scadenza_bollo || f.scadenza_bollo,
        scadenza_manutenzione: d.scadenza_manutenzione || f.scadenza_manutenzione,
      }));
      toast.success(`Dati ${d.source === 'database' ? 'caricati dal DB' : 'stimati'}. Verifica e modifica.`);
    } catch (err) { toast.error("Lookup fallito"); }
  };

  // brand list memo — filtra per categoria (auto / moto / scooter) se selezionata
  const brandOptions = useMemo(() => {
    const cat = form.tipo_veicolo;
    if (cat === "moto" || cat === "scooter") {
      return Array.from(new Set(ALL_BRANDS.filter((b) => b.category === cat).map((b) => b.name))).sort();
    }
    return CAR_BRANDS.map((b) => b.name);
  }, [form.tipo_veicolo]);
  const modelOptions = useMemo(() => {
    const cat = form.tipo_veicolo === "moto" || form.tipo_veicolo === "scooter" ? form.tipo_veicolo : "auto";
    return getModelsByBrand(form.marca, cat);
  }, [form.marca, form.tipo_veicolo]);

  const addDanno = () => {
    set("danni", [...(form.danni || []), { data: new Date().toISOString().slice(0, 10), descrizione: "", gravita: "lieve", costo: 0, riparato: false }]);
  };
  const updateDanno = (i, key, val) => {
    const arr = [...form.danni];
    arr[i] = { ...arr[i], [key]: val };
    set("danni", arr);
  };
  const removeDanno = (i) => {
    set("danni", form.danni.filter((_, idx) => idx !== i));
  };

  const toggleUscita = () => {
    if (form.uscita_flotta) set("uscita_flotta", null);
    else set("uscita_flotta", { data: new Date().toISOString().slice(0, 10), causa: "vendita", note: "", valore_realizzo: 0 });
  };

  return (
    <div className="space-y-6" data-testid="fleet-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Flotta</h1>
          <p className="text-sm text-white/45 mt-1">
            {cars.length} veicoli ·
            <span className="ml-2 text-emerald-300">{cars.filter((c) => statoScadenze(c) === "ok").length} OK</span> ·
            <span className="ml-2 text-amber-300">{cars.filter((c) => statoScadenze(c) === "warning").length} in scadenza</span> ·
            <span className="ml-2 text-[#ff5577]">{cars.filter((c) => statoScadenze(c) === "critico").length} scadute</span>
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Toggle vista */}
          <div className="inline-flex rounded-full bg-white/[0.03] border border-white/10 p-0.5" data-testid="fleet-view-toggle">
            <button onClick={() => setView("dashboard")} data-testid="fleet-view-dashboard"
              className={`text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full transition ${view === "dashboard" ? "bg-[#ff0033] text-white" : "text-white/60 hover:text-white"}`}>
              📋 Dashboard
            </button>
            <button onClick={() => setView("cards")} data-testid="fleet-view-cards"
              className={`text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full transition ${view === "cards" ? "bg-[#ff0033] text-white" : "text-white/60 hover:text-white"}`}>
              🖼️ Foto
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid="fleet-search" placeholder="Cerca…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
          </div>
          <button data-testid="fleet-add" onClick={openCreate}
            className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
            <Plus size={14} /> Aggiungi Auto
          </button>
          <button data-testid="fleet-import-real" onClick={async () => {
            if (!confirm("Importare la flotta reale FABRIRENT (51 veicoli)?\n\nATTENZIONE: questo CANCELLA tutti i veicoli demo esistenti.")) return;
            try {
              const r = await api.post("/cars/bulk-import-fabrirent?wipe_demo=true");
              toast.success(`Flotta importata: ${r.data.inserted} veicoli`);
              load();
            } catch (e) { toast.error("Errore import: " + (e?.response?.data?.detail || e.message)); }
          }} className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25">
            <Upload size={14} /> Importa Flotta Reale
          </button>
        </div>
      </div>

      {/* === DASHBOARD VIEW (no foto) === */}
      {view === "dashboard" && (
        <div className="glass rounded-2xl overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0" data-testid="fleet-dashboard-table">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
              <tr>
                <th className="text-left p-3">Veicolo</th>
                <th className="text-left p-3">Alim. · Anno</th>
                <th className="text-center p-3">Assicurazione</th>
                <th className="text-center p-3">Bollo</th>
                <th className="text-center p-3">Revisione</th>
                <th className="text-center p-3">Manutenzione</th>
                <th className="text-center p-3">Stato</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {cars.filter((c) => {
                if (!search) return true;
                const q = search.toLowerCase();
                return [c.marca, c.modello, c.targa, c.carburante].some((x) => (x || "").toLowerCase().includes(q));
              }).map((c) => {
                const bAss = scadenzaBadge(c.scadenza_assicurazione);
                const bBol = scadenzaBadge(c.scadenza_bollo);
                const bRev = scadenzaBadge(c.scadenza_revisione);
                const bMan = scadenzaBadge(c.scadenza_manutenzione);
                const st = statoScadenze(c);
                const stCfg = {
                  ok: { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", label: "🟢 OK" },
                  warning: { cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", label: "🟡 Attenzione" },
                  critico: { cls: "bg-[#ff0033]/15 text-[#ff5577] border-[#ff0033]/40", label: "🔴 Scadute" },
                  incompleto: { cls: "bg-white/10 text-white/50 border-white/20", label: "⚪ Dati incompleti" },
                }[st];
                return (
                  <tr key={c.id} className="hover:bg-white/[0.02]" data-testid={`fleet-row-${c.targa}`}>
                    <td className="p-3">
                      <div className="font-medium">{c.marca} {c.modello}</div>
                      <div className="text-[10px] font-mono text-white/40">{c.targa}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-xs">{c.carburante}</div>
                      <div className="text-[10px] text-white/40">{c.anno_immatricolazione || c.anno}</div>
                    </td>
                    {[
                      { b: bAss, d: c.scadenza_assicurazione },
                      { b: bBol, d: c.scadenza_bollo },
                      { b: bRev, d: c.scadenza_revisione },
                      { b: bMan, d: c.scadenza_manutenzione },
                    ].map((x, i) => (
                      <td key={i} className="p-3 text-center">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border whitespace-nowrap ${x.b.cls}`}>{x.b.label}</span>
                        {x.d && <div className="text-[10px] font-mono text-white/40 mt-1">{x.d}</div>}
                      </td>
                    ))}
                    <td className="p-3 text-center">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border whitespace-nowrap ${stCfg.cls}`}>{stCfg.label}</span>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button data-testid={`edit-${c.targa}`} onClick={() => startEdit(c)} className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-white/10 text-white/60 hover:text-white">
                        <Pencil size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {cars.length === 0 && (
                <tr><td colSpan="8" className="p-10 text-center text-white/40">Nessun veicolo in flotta.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* === CARDS VIEW (con foto, classica) === */}
      {view === "cards" && (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {cars.map((c, i) => {
          const logo = getBrandLogo(c.marca);
          return (
            <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="glass rounded-2xl overflow-hidden group hover:border-[#ff0033]/40 transition-all"
              data-testid={`car-${c.targa}`}>
              <div className="aspect-[16/10] bg-black overflow-hidden relative">
                <img src={c.foto?.[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                {logo && (
                  <div className="absolute top-2 left-2 bg-white/95 rounded-md p-1 backdrop-blur-sm shadow-lg">
                    <img src={logo} alt={c.marca} className="h-5 w-auto object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
                {c.proprieta_mezzo && c.proprieta_mezzo !== "aziendale" && (
                  <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-[9px] uppercase tracking-widest px-2 py-1 rounded-full border border-white/20">
                    {(PROPRIETA_TYPES.find((p) => p.value === c.proprieta_mezzo)?.label) || c.proprieta_mezzo}
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">{c.marca} {c.modello}</div>
                    <div className="text-xs text-white/40 font-mono">{c.targa} · {c.anno}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${STATO_BADGE[c.stato] || STATO_BADGE.fuori_servizio}`}>{c.stato}</span>
                </div>
                <div className="grid grid-cols-3 gap-1 mt-3 text-[10px] text-white/45 font-mono uppercase">
                  <div className="flex items-center gap-1"><Fuel size={11} />{(c.carburante || "").slice(0, 4)}</div>
                  <div className="flex items-center gap-1"><Settings2 size={11} />{(c.cambio || "").slice(0, 4)}</div>
                  <div className="flex items-center gap-1"><Gauge size={11} />{c.km?.toLocaleString()}km</div>
                </div>
                <div className="flex items-end justify-between mt-4 pt-3 border-t border-white/5">
                  <div>
                    <div className="font-display text-xl font-extrabold neon-text">€{c.prezzo_giornaliero}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40">/giorno</div>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`edit-${c.targa}`} onClick={() => startEdit(c)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/10 text-white/60 hover:text-white">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={`delete-${c.targa}`} onClick={() => remove(c.id)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      )}

      <AnimatePresence>
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 md:p-6"
             onClick={() => setShowForm(false)}>
          <motion.form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
            className="w-full max-w-5xl bg-[#0a0a0a] border border-white/10 rounded-3xl overflow-hidden max-h-[92vh] flex flex-col shadow-[0_0_120px_-20px_#ff0033]"
            data-testid="fleet-form">
            {/* Header */}
            <div className="flex items-center justify-between px-7 py-5 border-b border-white/5 bg-gradient-to-r from-[#ff0033]/[0.06] via-transparent to-transparent">
              <div>
                <div className="font-display text-2xl font-bold tracking-tight">{editId ? "Modifica Veicolo" : "Nuovo Veicolo in Flotta"}</div>
                <div className="text-xs text-white/40 mt-0.5">Compila tutte le sezioni per un tracciamento professionale</div>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 rounded-full hover:bg-white/10" data-testid="form-close">
                <X size={18} />
              </button>
            </div>

            {/* Body: sidebar + content */}
            <div className="flex flex-1 overflow-hidden">
              {/* Sidebar sections */}
              <div className="hidden md:flex flex-col w-56 border-r border-white/5 bg-black/40 py-4">
                {SECTIONS.map((s) => {
                  const Ico = s.icon;
                  const active = activeSection === s.id;
                  return (
                    <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
                      data-testid={`sec-${s.id}`}
                      className={`flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-widest border-l-2 transition-all ${active ? "border-[#ff0033] text-white bg-[#ff0033]/[0.05]" : "border-transparent text-white/45 hover:text-white/80 hover:bg-white/[0.02]"}`}>
                      <Ico size={14} />
                      <span className="truncate">{s.label}</span>
                      {active && <ChevronRight size={12} className="ml-auto" />}
                    </button>
                  );
                })}
              </div>

              {/* Mobile tabs */}
              <div className="md:hidden flex overflow-x-auto border-b border-white/5 bg-black/40 px-2">
                {SECTIONS.map((s) => {
                  const active = activeSection === s.id;
                  return (
                    <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
                      className={`shrink-0 px-3 py-3 text-[10px] uppercase tracking-widest border-b-2 ${active ? "border-[#ff0033] text-white" : "border-transparent text-white/45"}`}>
                      {s.label}
                    </button>
                  );
                })}
              </div>

              {/* Section content */}
              <div className="flex-1 overflow-auto p-6 md:p-7">

                {/* === IDENTIFICATIVI === */}
                {activeSection === "ident" && (
                  <div className="space-y-5">
                    {/* SCANNER LIBRETTO */}
                    {!editId && (
                      <DocumentScannerBox context="fleet"
                        testIdPrefix="fleet-scan"
                        title="Scansione AI · Libretto di Circolazione"
                        subtitle="→ Auto-compila Targa + Marca + Modello + Telaio"
                        description="Carica foto o PDF del libretto di circolazione (carta di circolazione, CdP). L'AI estrae targa, telaio, marca, modello, cilindrata, alimentazione, anno immatricolazione."
                        variant="compact"
                        onResult={(payload) => {
                          const v = payload?.veicolo || {};
                          setForm((f) => ({
                            ...f,
                            targa: (v.targa || f.targa || "").toUpperCase(),
                            marca: v.marca || f.marca,
                            modello: v.modello || f.modello,
                            telaio: v.telaio || f.telaio,
                            cilindrata: v.cilindrata || f.cilindrata,
                            carburante: v.carburante || v.alimentazione || f.carburante,
                            anno_immatricolazione: v.anno || v.anno_immatricolazione || f.anno_immatricolazione,
                            potenza_kw: v.potenza_kw || f.potenza_kw,
                            colore: v.colore || f.colore,
                          }));
                        }}
                      />
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <Field label="Tipo veicolo">
                        <select value={form.tipo_veicolo} onChange={(e) => set("tipo_veicolo", e.target.value)} className={selectCls}>
                          {["auto", "scooter", "moto", "furgone", "van"].map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </Field>

                      <Field label="Marca" testid="marca">
                        <SearchableSelect
                          value={form.marca}
                          onChange={(v) => set("marca", v)}
                          options={brandOptions}
                          placeholder="Cerca marca… (es. Fiat, Ducati, Vespa)"
                          getLogo={getBrandLogo}
                          testid="marca-search"
                        />
                        {form.marca && getBrandLogo(form.marca) && (
                          <div className="mt-2 flex items-center gap-2 text-[10px] text-white/40">
                            <img src={getBrandLogo(form.marca)} alt="" className="h-5 bg-white rounded px-1 py-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
                            <span>Logo rilevato</span>
                          </div>
                        )}
                      </Field>

                      <Field label="Modello" testid="modello">
                        <SearchableSelect
                          value={form.modello}
                          onChange={(v) => set("modello", v)}
                          options={modelOptions}
                          placeholder={modelOptions.length ? `Cerca modello… (${modelOptions.length} disponibili)` : "Seleziona prima la marca, poi cerca modello"}
                          allowFreeText
                          testid="modello-search"
                        />
                      </Field>

                      <Field label="Targa" testid="targa">
                        <div className="flex gap-2">
                          <input value={form.targa} onChange={(e) => set("targa", e.target.value.toUpperCase())}
                            className={inputCls} placeholder="AA000BB" />
                          <button type="button" onClick={lookupTarga} data-testid="targa-lookup"
                            className="mt-1 px-3 rounded-lg bg-[#ff0033]/15 border border-[#ff0033]/30 text-[#ff5577] hover:bg-[#ff0033]/25 text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
                            <Sparkles size={11} /> Lookup
                          </button>
                        </div>
                      </Field>

                      <Field label="Anno modello"><input type="number" value={form.anno} onChange={(e) => set("anno", e.target.value)} className={inputCls} /></Field>
                      <Field label="Anno immatricolazione"><input type="number" value={form.anno_immatricolazione} onChange={(e) => set("anno_immatricolazione", e.target.value)} className={inputCls} /></Field>
                      <Field label="Km attuali"><input type="number" value={form.km} onChange={(e) => set("km", e.target.value)} className={inputCls} /></Field>
                      <Field label="Colore"><input value={form.colore} onChange={(e) => set("colore", e.target.value)} className={inputCls} /></Field>

                      <Field label="Carburante">
                        <select value={form.carburante} onChange={(e) => set("carburante", e.target.value)} className={selectCls}>
                          {["Benzina", "Diesel", "Ibrida", "Elettrica"].map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </Field>
                      <Field label="Cambio">
                        <select value={form.cambio} onChange={(e) => set("cambio", e.target.value)} className={selectCls}>
                          {["Manuale", "Automatico"].map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </Field>
                      <Field label="Stato">
                        <select value={form.stato} onChange={(e) => set("stato", e.target.value)} className={selectCls}>
                          {["disponibile", "noleggiata", "manutenzione", "guasto", "fuori_servizio", "fuori_flotta"].map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </Field>
                      {(form.stato === "guasto" || form.stato === "fuori_servizio" || form.stato === "manutenzione") && (
                        <Field label="Motivo fuori uso" className="col-span-2 md:col-span-3">
                          <input value={form.motivo_fuori_uso} onChange={(e) => set("motivo_fuori_uso", e.target.value)} className={inputCls} placeholder="Es. cambio gomme, frizione bruciata…" />
                        </Field>
                      )}
                    </div>

                    <div className="border-t border-white/5 pt-4">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Tariffe</div>
                      <div className="grid grid-cols-3 gap-3">
                        <Field label="€/giorno"><input type="number" value={form.prezzo_giornaliero} onChange={(e) => set("prezzo_giornaliero", e.target.value)} className={inputCls} /></Field>
                        <Field label="€/settimana"><input type="number" value={form.prezzo_settimanale} onChange={(e) => set("prezzo_settimanale", e.target.value)} className={inputCls} /></Field>
                        <Field label="€/mese"><input type="number" value={form.prezzo_mensile} onChange={(e) => set("prezzo_mensile", e.target.value)} className={inputCls} /></Field>
                      </div>
                    </div>
                  </div>
                )}

                {/* === PROPRIETÀ === */}
                {activeSection === "prop" && (
                  <div className="space-y-5">
                    <Field label="Tipo proprietà mezzo">
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                        {PROPRIETA_TYPES.map((p) => (
                          <button key={p.value} type="button" onClick={() => set("proprieta_mezzo", p.value)}
                            data-testid={`prop-${p.value}`}
                            className={`px-3 py-3 rounded-xl text-xs uppercase tracking-wider border transition-all ${form.proprieta_mezzo === p.value ? "border-[#ff0033] bg-[#ff0033]/10 text-white" : "border-white/10 bg-white/[0.02] text-white/55 hover:border-white/30"}`}>
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </Field>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="Proprietario (nome / società)"><input value={form.proprietario_nome} onChange={(e) => set("proprietario_nome", e.target.value)} className={inputCls} placeholder="Es. Mario Rossi / Alpha Leasing S.p.A." /></Field>
                      <Field label="Contatto proprietario"><input value={form.proprietario_contatto} onChange={(e) => set("proprietario_contatto", e.target.value)} className={inputCls} placeholder="Telefono / email" /></Field>
                    </div>

                    {(form.proprieta_mezzo === "leasing" || form.proprieta_mezzo === "noleggio_lungo") && (
                      <div className="border-t border-white/5 pt-4">
                        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Dati Leasing / NLT</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <Field label="Società leasing"><input value={form.leasing_societa} onChange={(e) => set("leasing_societa", e.target.value)} className={inputCls} /></Field>
                          <Field label="Canone mensile (€)"><input type="number" value={form.leasing_canone_mensile} onChange={(e) => set("leasing_canone_mensile", e.target.value)} className={inputCls} /></Field>
                          <Field label="Scadenza contratto"><input type="date" value={form.leasing_scadenza} onChange={(e) => set("leasing_scadenza", e.target.value)} className={inputCls} /></Field>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* === ASSICURAZIONE === */}
                {activeSection === "ass" && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Field label="Compagnia assicurativa"><input value={form.dati_assicurativi.compagnia} onChange={(e) => set("dati_assicurativi.compagnia", e.target.value)} className={inputCls} placeholder="Es. Allianz / Generali" /></Field>
                      <Field label="Numero polizza"><input value={form.dati_assicurativi.numero_polizza} onChange={(e) => set("dati_assicurativi.numero_polizza", e.target.value)} className={inputCls} /></Field>
                      <Field label="Broker / Intermediario"><input value={form.dati_assicurativi.broker} onChange={(e) => set("dati_assicurativi.broker", e.target.value)} className={inputCls} /></Field>
                      <Field label="Data emissione"><input type="date" value={form.dati_assicurativi.data_emissione} onChange={(e) => set("dati_assicurativi.data_emissione", e.target.value)} className={inputCls} /></Field>
                      <Field label="Data scadenza"><input type="date" value={form.dati_assicurativi.data_scadenza} onChange={(e) => set("dati_assicurativi.data_scadenza", e.target.value)} className={inputCls} /></Field>
                      <Field label="Premio annuo (€)"><input type="number" value={form.dati_assicurativi.premio_annuo} onChange={(e) => set("dati_assicurativi.premio_annuo", e.target.value)} className={inputCls} /></Field>
                    </div>

                    <div className="border-t border-white/5 pt-4">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Franchigie & Massimali per copertura</div>
                      <div className="space-y-3">
                        {[
                          { k: "rca", label: "RCA — Responsabilità Civile" },
                          { k: "kasko", label: "Kasko" },
                          { k: "furto", label: "Furto" },
                          { k: "incendio", label: "Incendio" },
                        ].map((cov) => (
                          <div key={cov.k} className="rounded-xl bg-white/[0.02] border border-white/5 p-4">
                            <div className="text-xs font-bold mb-3 text-white/80">{cov.label}</div>
                            <div className="grid grid-cols-3 gap-3">
                              <Field label="Franchigia €"><input type="number" value={form.dati_assicurativi[cov.k]?.franchigia_eur || 0} onChange={(e) => set(`dati_assicurativi.${cov.k}.franchigia_eur`, +e.target.value)} className={inputCls} /></Field>
                              <Field label="Franchigia %"><input type="number" step="0.1" value={form.dati_assicurativi[cov.k]?.franchigia_perc || 0} onChange={(e) => set(`dati_assicurativi.${cov.k}.franchigia_perc`, +e.target.value)} className={inputCls} /></Field>
                              <Field label="Massimale €"><input type="number" value={form.dati_assicurativi[cov.k]?.massimale || 0} onChange={(e) => set(`dati_assicurativi.${cov.k}.massimale`, +e.target.value)} className={inputCls} /></Field>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-white/5 pt-4">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Garanzie accessorie</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {[
                          ["atti_vandalici", "Atti vandalici"],
                          ["cristalli", "Cristalli"],
                          ["assistenza_stradale", "Assistenza stradale"],
                          ["infortuni_conducente", "Infortuni conducente"],
                        ].map(([k, label]) => (
                          <label key={k} className="flex items-center gap-2 px-3 py-3 rounded-lg bg-white/[0.02] border border-white/5 cursor-pointer hover:bg-white/[0.04]">
                            <input type="checkbox" checked={!!form.dati_assicurativi[k]} onChange={(e) => set(`dati_assicurativi.${k}`, e.target.checked)} className="accent-[#ff0033]" />
                            <span className="text-xs">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-white/5 pt-4">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Scadenze veicolari</div>
                      <div className="grid grid-cols-3 gap-3">
                        <Field label="Scad. Assicurazione"><input type="date" value={form.scadenza_assicurazione || ""} onChange={(e) => set("scadenza_assicurazione", e.target.value)} className={inputCls} /></Field>
                        <Field label="Scad. Revisione"><input type="date" value={form.scadenza_revisione || ""} onChange={(e) => set("scadenza_revisione", e.target.value)} className={inputCls} /></Field>
                        <Field label="Scad. Bollo"><input type="date" value={form.scadenza_bollo || ""} onChange={(e) => set("scadenza_bollo", e.target.value)} className={inputCls} /></Field>
                        <Field label="Scad. Manutenzione (prossimo tagliando)"><input type="date" value={form.scadenza_manutenzione || ""} onChange={(e) => set("scadenza_manutenzione", e.target.value)} className={inputCls} /></Field>
                      </div>
                    </div>
                  </div>
                )}

                {/* === CICLO DI VITA === */}
                {activeSection === "cv" && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="N° Telaio (VIN)" testid="telaio">
                        <input value={form.nr_telaio || form.numero_telaio || ""}
                          onChange={(e) => { const v = e.target.value.toUpperCase(); set("nr_telaio", v); set("numero_telaio", v); }}
                          className={inputCls} placeholder="WVWZZZ..." maxLength={17} />
                      </Field>
                      <Field label="Data entrata flotta">
                        <input type="date" value={form.entrata_flotta || ""} onChange={(e) => set("entrata_flotta", e.target.value)} className={inputCls} />
                      </Field>
                      <Field label="Costo acquisto / immissione (€)">
                        <input type="number" value={form.costo_acquisto} onChange={(e) => set("costo_acquisto", e.target.value)} className={inputCls} />
                      </Field>
                      <Field label="GPS Tracker">
                        <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5">
                          <input type="checkbox" checked={form.gps_tracker} onChange={(e) => set("gps_tracker", e.target.checked)} className="accent-[#ff0033]" />
                          <span className="text-xs text-white/70">Tracker GPS attivo sul veicolo</span>
                        </div>
                      </Field>
                    </div>

                    <div className="border-t border-white/5 pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-[10px] uppercase tracking-widest text-white/40 inline-flex items-center gap-2"><ExitIcon size={12} /> Uscita dalla flotta</div>
                        <button type="button" onClick={toggleUscita}
                          data-testid="toggle-uscita"
                          className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${form.uscita_flotta ? "border-[#ff0033] text-[#ff5577]" : "border-white/15 text-white/50 hover:border-white/30"}`}>
                          {form.uscita_flotta ? "Annulla uscita" : "Registra uscita"}
                        </button>
                      </div>
                      {form.uscita_flotta && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-[#ff0033]/[0.04] border border-[#ff0033]/15 rounded-xl p-4">
                          <Field label="Data uscita">
                            <input type="date" value={form.uscita_flotta.data} onChange={(e) => set("uscita_flotta.data", e.target.value)} className={inputCls} />
                          </Field>
                          <Field label="Causa uscita">
                            <select value={form.uscita_flotta.causa} onChange={(e) => set("uscita_flotta.causa", e.target.value)} className={selectCls}>
                              <option value="vendita">Vendita</option>
                              <option value="fine_leasing">Fine leasing</option>
                              <option value="fine_nlt">Fine NLT</option>
                              <option value="restituzione">Restituzione proprietario</option>
                              <option value="furto">Furto</option>
                              <option value="incidente_irreparabile">Incidente irreparabile</option>
                              <option value="rottamazione">Rottamazione</option>
                              <option value="altro">Altro</option>
                            </select>
                          </Field>
                          <Field label="Valore di realizzo (€)">
                            <input type="number" value={form.uscita_flotta.valore_realizzo} onChange={(e) => set("uscita_flotta.valore_realizzo", +e.target.value)} className={inputCls} />
                          </Field>
                          <Field label="Note" className="md:col-span-1">
                            <input value={form.uscita_flotta.note} onChange={(e) => set("uscita_flotta.note", e.target.value)} className={inputCls} />
                          </Field>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* === DANNI INIZIALI === */}
                {activeSection === "danni" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-white/60">Registra danni preesistenti al momento dell'inserimento in flotta. Per la timeline completa post-creazione utilizza la sezione <strong className="text-white">Gestione Danni</strong>.</div>
                      <button type="button" onClick={addDanno} data-testid="add-danno"
                        className="px-4 py-2 rounded-full text-[10px] uppercase tracking-widest bg-[#ff0033]/15 border border-[#ff0033]/30 text-[#ff5577] hover:bg-[#ff0033]/25 inline-flex items-center gap-2">
                        <Plus size={12} /> Aggiungi danno
                      </button>
                    </div>

                    {(!form.danni || form.danni.length === 0) ? (
                      <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-white/40 text-sm">
                        Nessun danno registrato. Il veicolo entra in flotta integro.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {form.danni.map((d, i) => (
                          <div key={i} className="grid grid-cols-12 gap-2 items-end rounded-lg bg-white/[0.02] border border-white/5 p-3" data-testid={`danno-${i}`}>
                            <Field label="Data" className="col-span-3">
                              <input type="date" value={d.data} onChange={(e) => updateDanno(i, "data", e.target.value)} className={inputCls} />
                            </Field>
                            <Field label="Descrizione" className="col-span-5">
                              <input value={d.descrizione} onChange={(e) => updateDanno(i, "descrizione", e.target.value)} className={inputCls} placeholder="Es. graffio paraurti anteriore destro" />
                            </Field>
                            <Field label="Gravità" className="col-span-2">
                              <select value={d.gravita} onChange={(e) => updateDanno(i, "gravita", e.target.value)} className={selectCls}>
                                <option value="lieve">Lieve</option>
                                <option value="medio">Medio</option>
                                <option value="grave">Grave</option>
                              </select>
                            </Field>
                            <Field label="Costo €" className="col-span-1">
                              <input type="number" value={d.costo} onChange={(e) => updateDanno(i, "costo", +e.target.value)} className={inputCls} />
                            </Field>
                            <div className="col-span-1 flex justify-end">
                              <button type="button" onClick={() => removeDanno(i)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]" data-testid={`remove-danno-${i}`}>
                                <Trash size={13} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* === MEDIA === */}
                {activeSection === "media" && (
                  <div className="space-y-5">
                    <Field label="URL Foto principale">
                      <input value={form.foto[0] || ""} onChange={(e) => set("foto", [e.target.value])} className={inputCls} placeholder="https://…" />
                      {form.foto[0] && (
                        <div className="mt-3 rounded-xl overflow-hidden border border-white/5">
                          <img src={form.foto[0]} alt="" className="w-full max-h-64 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                      )}
                    </Field>
                    <Field label="Descrizione veicolo">
                      <textarea rows={4} value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)} className={inputCls} placeholder="Note pubbliche, optional, dotazioni…" />
                    </Field>
                    <Field label="Optional (separati da virgola)">
                      <input value={(form.optional || []).join(", ")} onChange={(e) => set("optional", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={inputCls} placeholder="Es. Bluetooth, Sensori parcheggio, Tetto panoramico" />
                    </Field>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-7 py-4 border-t border-white/5 justify-end bg-black/40">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10 hover:bg-white/10">Annulla</button>
              <button data-testid="fleet-form-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">{editId ? "Salva modifiche" : "Aggiungi alla flotta"}</button>
            </div>
          </motion.form>
        </div>
      )}
      </AnimatePresence>
    </div>
  );
}
